/**
 * Document save action hooks for document state management
 *
 * These hooks provide a Zustand-style API for document save actions,
 * dispatching directly to the Zustand document store.
 *
 * NOTE: writeDocument is a "signal" action that is not processed through the
 * document store's reduce() method, so the service notification system won't see it.
 * We must manually call documentSaveService.onAction() to ensure saves happen.
 */
import type {IDocumentData} from "@buildwithflux/models";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentSaveService} from "modules/document/effects/DocumentSaveService";
import {writeDocument} from "modules/document/state/actionCreators/documentActions";
import type {DocumentStore} from "modules/document/state/document";

/**
 * NOTE: writeDocument is a "signal" action that is NOT processed by the document
 * store's action handler chain. Its sole purpose is to tell DocumentSaveService
 * to queue a save. We call documentSaveService.onAction() directly instead of
 * going through dispatch(), which would be a no-op for this action type.
 */

/**
 * Hook to get the writeDocument action
 *
 * Dispatches to the Zustand document store and notifies DocumentSaveService.
 * This action writes the document state to the save queue.
 *
 * @returns A function to write the document state
 */
export function useWriteDocument(): (documentState: IDocumentData, overrideFeatureFlag?: boolean) => void {
    const {documentStore, documentSaveService} = useFluxServices();

    return useCallback(
        (documentState: IDocumentData, overrideFeatureFlag = false) => {
            const action = writeDocument(documentState, overrideFeatureFlag);
            documentSaveService.onAction(action);
        },
        [documentSaveService],
    );
}

/**
 * Hook to get the writeDocumentToFirebase action
 *
 * Dispatches to the Zustand document store and notifies DocumentSaveService.
 * This action writes the current document state to Firebase.
 * Used as an affordance when auto-save is disabled.
 *
 * @returns A function to write the document to Firebase
 */
export function useWriteDocumentToFirebase(): () => void {
    const {documentStore, documentSaveService} = useFluxServices();

    return useCallback(() => {
        const documentData = documentStore.getState().document;
        if (documentData) {
            const action = writeDocument(documentData, false);
            documentSaveService.onAction(action);
        }
    }, [documentStore, documentSaveService]);
}

/**
 * Factory function to create document save actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch document save actions.
 *
 * @param documentStore - The document dispatcher from FluxServices
 * @param documentStore - The document store from FluxServices
 * @param documentSaveService - The document save service from FluxServices
 * @returns An object with document save action methods
 */
export function createDocumentSaveActions(documentStore: DocumentStore, documentSaveService: DocumentSaveService) {
    return {
        writeDocument: (documentState: IDocumentData, overrideFeatureFlag = false) => {
            const action = writeDocument(documentState, overrideFeatureFlag);
            documentSaveService.onAction(action);
        },
        writeDocumentToFirebase: () => {
            const documentData = documentStore.getState().document;
            if (documentData) {
                const action = writeDocument(documentData, false);
                documentSaveService.onAction(action);
            }
        },
    };
}
